import React, { useState, useEffect } from 'react';
import { 
  FaBell, FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, 
  FaArrowLeft, FaVideo, FaFile, FaImage, FaLink,
  FaCalendarAlt, FaGlobe, FaSave, FaTimes,
  FaSpinner
} from 'react-icons/fa';
import { 
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, 
  query, orderBy, Timestamp 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './GerenciarAvisos.css';

function GerenciarAvisos() {
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAviso, setEditingAviso] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    tipo: 'texto', // texto, texto_imagem, texto_link, video, video_texto
    conteudo: '',
    imagemUrl: '',
    linkUrl: '',
    videoUrl: '',
    showPopup: false,
    dataInicio: '',
    dataFim: '',
    ativo: true
  });
  const [submitting, setSubmitting] = useState(false);

  const tipoExigeConteudo = (tipo) => (
    tipo === 'texto' || tipo === 'texto_link' || tipo === 'video_texto'
  );

  useEffect(() => {
    carregarAvisos();
  }, []);

  const carregarAvisos = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'avisos'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const avisosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dataInicio: doc.data().dataInicio?.toDate?.() || new Date(doc.data().dataInicio),
        dataFim: doc.data().dataFim?.toDate?.() || new Date(doc.data().dataFim),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      setAvisos(avisosData);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
      alert('Erro ao carregar avisos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      tipo: 'texto',
      conteudo: '',
      imagemUrl: '',
      linkUrl: '',
      videoUrl: '',
      showPopup: false,
      dataInicio: '',
      dataFim: '',
      ativo: true
    });
    setEditingAviso(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const titulo = formData.titulo.trim();
    const conteudo = formData.conteudo.trim();
    const imagemUrl = formData.imagemUrl.trim();
    const linkUrl = formData.linkUrl.trim();
    const videoUrl = formData.videoUrl.trim();

    if (!titulo) {
      alert('Título é obrigatório');
      return;
    }

    if (tipoExigeConteudo(formData.tipo) && !conteudo) {
      alert('Conteúdo é obrigatório para esse tipo de aviso');
      return;
    }

    if (formData.tipo === 'texto_imagem' && !imagemUrl) {
      alert('URL da imagem é obrigatória para aviso com imagem');
      return;
    }

    if ((formData.tipo === 'video' || formData.tipo === 'video_texto') && !videoUrl) {
      alert('URL do vídeo é obrigatória para aviso com vídeo');
      return;
    }

    if (formData.tipo === 'texto_link' && !linkUrl) {
      alert('URL do link é obrigatória para aviso com link');
      return;
    }

    if (!formData.dataInicio || !formData.dataFim) {
      alert('Data de início e fim são obrigatórias');
      return;
    }

    if (new Date(formData.dataFim) <= new Date(formData.dataInicio)) {
      alert('Data de fim deve ser posterior à data de início');
      return;
    }

    setSubmitting(true);

    try {
      const avisoData = {
        titulo,
        tipo: formData.tipo,
        conteudo,
        imagemUrl,
        linkUrl,
        videoUrl,
        showPopup: formData.showPopup,
        dataInicio: Timestamp.fromDate(new Date(formData.dataInicio)),
        dataFim: Timestamp.fromDate(new Date(formData.dataFim)),
        ativo: formData.ativo,
        updatedAt: Timestamp.now()
      };

      if (editingAviso) {
        await updateDoc(doc(db, 'avisos', editingAviso.id), avisoData);
        alert('Aviso atualizado com sucesso!');
      } else {
        await addDoc(collection(db, 'avisos'), {
          ...avisoData,
          createdAt: Timestamp.now(),
          visualizacoes: []
        });
        alert('Aviso criado com sucesso!');
      }

      setShowModal(false);
      resetForm();
      carregarAvisos();
    } catch (error) {
      console.error('Erro ao salvar aviso:', error);
      alert('Erro ao salvar aviso');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (aviso) => {
    setEditingAviso(aviso);
    setFormData({
      titulo: aviso.titulo,
      tipo: aviso.tipo,
      conteudo: aviso.conteudo,
      imagemUrl: aviso.imagemUrl || '',
      linkUrl: aviso.linkUrl || '',
      videoUrl: aviso.videoUrl || '',
      showPopup: aviso.showPopup || false,
      dataInicio: aviso.dataInicio.toISOString().slice(0, 16),
      dataFim: aviso.dataFim.toISOString().slice(0, 16),
      ativo: aviso.ativo
    });
    setShowModal(true);
  };

  const handleDelete = async (avisoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este aviso?')) return;

    try {
      await deleteDoc(doc(db, 'avisos', avisoId));
      alert('Aviso excluído com sucesso!');
      carregarAvisos();
    } catch (error) {
      console.error('Erro ao excluir aviso:', error);
      alert('Erro ao excluir aviso');
    }
  };

  const toggleAtivo = async (aviso) => {
    try {
      await updateDoc(doc(db, 'avisos', aviso.id), {
        ativo: !aviso.ativo,
        updatedAt: Timestamp.now()
      });
      carregarAvisos();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status do aviso');
    }
  };

  const expirarAviso = async (aviso) => {
    try {
      await updateDoc(doc(db, 'avisos', aviso.id), {
        ativo: false,
        dataFim: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      carregarAvisos();
    } catch (error) {
      console.error('Erro ao expirar aviso:', error);
      alert('Erro ao expirar aviso');
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'video': return <FaVideo />;
      case 'texto_imagem': return <FaImage />;
      case 'texto_link': return <FaLink />;
      case 'video_texto': return <FaVideo />;
      default: return <FaFile />;
    }
  };

  const getStatusAviso = (aviso) => {
    const agora = new Date();
    const inicio = aviso.dataInicio;
    const fim = aviso.dataFim;

    if (!aviso.ativo) return { status: 'Inativo', className: 'status-inativo' };
    if (agora < inicio) return { status: 'Agendado', className: 'status-agendado' };
    if (agora > fim) return { status: 'Expirado', className: 'status-expirado' };
    return { status: 'Ativo', className: 'status-ativo' };
  };

  if (loading) {
    return (
      <div className="gerenciar-avisos-container">
        <div className="loading-spinner">
          <FaSpinner className="spinner" />
          <p>Carregando avisos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gerenciar-avisos-container">
      <header className="page-header">
        <button onClick={() => window.history.back()} className="back-button">
          <FaArrowLeft />
        </button>
        <div className="header-content">
          <h1>
            <FaBell className="header-icon" />
            Gerenciar Avisos e Notificações
          </h1>
          <p>Crie e gerencie avisos para os usuários do sistema</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="add-button"
        >
          <FaPlus />
          Novo Aviso
        </button>
      </header>

      {/* Estatísticas */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-number">{avisos.length}</div>
          <div className="stat-label">Total de Avisos</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {avisos.filter(a => {
              const agora = new Date();
              return a.ativo && agora >= a.dataInicio && agora <= a.dataFim;
            }).length}
          </div>
          <div className="stat-label">Avisos Ativos</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {avisos.filter(a => a.showPopup && a.ativo).length}
          </div>
          <div className="stat-label">Com Popup</div>
        </div>
      </div>

      {/* Lista de Avisos */}
      <div className="avisos-grid">
        {avisos.length === 0 ? (
          <div className="empty-state">
            <FaBell className="empty-icon" />
            <h3>Nenhum aviso criado</h3>
            <p>Comece criando seu primeiro aviso para os usuários</p>
            <button onClick={() => setShowModal(true)} className="create-first-button">
              <FaPlus />
              Criar Primeiro Aviso
            </button>
          </div>
        ) : (
          avisos.map(aviso => {
            const statusInfo = getStatusAviso(aviso);
            return (
              <div key={aviso.id} className="aviso-card">
                <div className="card-header">
                  <div className="tipo-icon">
                    {getTipoIcon(aviso.tipo)}
                  </div>
                  <div className={`status-badge ${statusInfo.className}`}>
                    {statusInfo.status}
                  </div>
                  {aviso.showPopup && (
                    <div className="popup-badge">
                      <FaGlobe />
                      Popup
                    </div>
                  )}
                </div>

                <div className="card-content">
                  <h3 className="aviso-titulo">{aviso.titulo}</h3>
                  <p className="aviso-conteudo">
                    {aviso.conteudo.length > 100 
                      ? `${aviso.conteudo.substring(0, 100)}...`
                      : aviso.conteudo
                    }
                  </p>
                  
                  <div className="aviso-dates">
                    <div className="date-item">
                      <FaCalendarAlt />
                      <span>
                        {aviso.dataInicio.toLocaleDateString('pt-BR')} - {aviso.dataFim.toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  {aviso.visualizacoes && (
                    <div className="visualizacoes-count">
                      <FaEye />
                      <span>{aviso.visualizacoes.length} visualizações</span>
                    </div>
                  )}
                </div>

                <div className="card-actions">
                  <button
                    onClick={() => toggleAtivo(aviso)}
                    className={`toggle-button ${aviso.ativo ? 'active' : 'inactive'}`}
                    title={aviso.ativo ? 'Desativar' : 'Ativar'}
                  >
                    {aviso.ativo ? <FaEye /> : <FaEyeSlash />}
                    <span>{aviso.ativo ? 'Desativar' : 'Ativar'}</span>
                  </button>
                  <button
                    onClick={() => expirarAviso(aviso)}
                    className="expire-button"
                    title="Expirar agora"
                    disabled={statusInfo.status === 'Expirado'}
                  >
                    <FaCalendarAlt />
                    <span>Expirar</span>
                  </button>
                  <button
                    onClick={() => handleEdit(aviso)}
                    className="edit-button"
                    title="Editar"
                  >
                    <FaEdit />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => handleDelete(aviso.id)}
                    className="delete-button"
                    title="Excluir"
                  >
                    <FaTrash />
                    <span>Excluir</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAviso ? 'Editar Aviso' : 'Novo Aviso'}</h2>
              <button 
                onClick={() => !submitting && setShowModal(false)}
                className="modal-close"
                disabled={submitting}
              >
                <FaTimes />
              </button>
            </div>

            <div onSubmit={handleSubmit} className="aviso-form">
              <div className="form-group">
                <label htmlFor="titulo">Título do Aviso *</label>
                <input
                  type="text"
                  id="titulo"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleInputChange}
                  placeholder="Digite o título do aviso"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tipo">Tipo de Aviso *</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  disabled={submitting}
                  required
                >
                  <option value="texto">Apenas Texto</option>
                  <option value="texto_imagem">Texto com Imagem</option>
                  <option value="texto_link">Texto com Link</option>
                  <option value="video">Apenas Vídeo</option>
                  <option value="video_texto">Vídeo com Texto</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="conteudo">
                  Conteúdo {tipoExigeConteudo(formData.tipo) ? '*' : '(opcional)'}
                </label>
                <textarea
                  id="conteudo"
                  name="conteudo"
                  value={formData.conteudo}
                  onChange={handleInputChange}
                  placeholder="Digite o conteúdo do aviso"
                  rows="4"
                  required={tipoExigeConteudo(formData.tipo)}
                  disabled={submitting}
                />
              </div>

              {formData.tipo === 'texto_imagem' && (
                <div className="form-group">
                  <label htmlFor="imagemUrl">URL da Imagem *</label>
                  <input
                    type="url"
                    id="imagemUrl"
                    name="imagemUrl"
                    value={formData.imagemUrl}
                    onChange={handleInputChange}
                    placeholder="https://exemplo.com/imagem.jpg"
                    disabled={submitting}
                    required={formData.tipo === 'texto_imagem'}
                  />
                </div>
              )}

              {formData.tipo === 'texto_link' && (
                <div className="form-group">
                  <label htmlFor="linkUrl">URL do Link *</label>
                  <input
                    type="url"
                    id="linkUrl"
                    name="linkUrl"
                    value={formData.linkUrl}
                    onChange={handleInputChange}
                    placeholder="https://exemplo.com"
                    disabled={submitting}
                    required={formData.tipo === 'texto_link'}
                  />
                </div>
              )}

              {(formData.tipo === 'video' || formData.tipo === 'video_texto') && (
                <div className="form-group">
                  <label htmlFor="videoUrl">URL do Vídeo *</label>
                  <input
                    type="url"
                    id="videoUrl"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/watch?v=..."
                    disabled={submitting}
                    required={formData.tipo === 'video' || formData.tipo === 'video_texto'}
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dataInicio">Data de Início *</label>
                  <input
                    type="datetime-local"
                    id="dataInicio"
                    name="dataInicio"
                    value={formData.dataInicio}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dataFim">Data de Fim *</label>
                  <input
                    type="datetime-local"
                    id="dataFim"
                    name="dataFim"
                    value={formData.dataFim}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="showPopup"
                    checked={formData.showPopup}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                  <span>Exibir como popup na página inicial</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                  <span>Aviso ativo</span>
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="cancel-button"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <>
                      <FaSpinner className="spinner" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {editingAviso ? 'Atualizar' : 'Criar'} Aviso
                    </>
                  )}
                </button>
                              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarAvisos;
